"use strict";
/*
  Email (MIME) parsing library. Copyright (C) 2020-2021 Vereign AG

  This program is free software: you can redistribute it and/or modify it under the terms of the GNU Affero General
  Public License as published by the Free Software Foundation, either version 3 of the License, or (at your option)
  any later version.

  This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY; without even the implied
  warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
  details.

  You should have received a copy of the GNU Affero General Public License along with this program. If not, see
  https://www.gnu.org/licenses/.

  In case of queries or questions, please contact us under by email under contact@vereign.com or by letter post to
  Vereign AG, Kolinplatz 10, 6300 Zug, Switzerland
*/
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
/* eslint-disable */
const constants_1 = require("./constants");
const libmime_1 = require("./utils/libmime");
const libqp_1 = require("./utils/libqp");
const utils_1 = require("./utils");
const fwdRplyExtractor_1 = require("./utils/fwdRplyExtractor");
const charset_1 = require("./utils/charset");
const newline = /\r\n|\r|\n/g;
const DEFAULT_ATTACHMENT_NAME = "attachment";
const SMIMEStart = "This is a cryptographically signed message in MIME format.\r\n\r\n";
class MIMEParser {
    constructor(mimeString) {
        this.mimeString = fixNewLines(mimeString);
        this.parts = parseMIME(this.mimeString);
        this.parseHTML = (htmlString) => {
            if (typeof DOMParser === "undefined") {
                throw new Error('DOMParser is not available in target environment. Please provide custom "parseHTML" function.');
            }
            const parser = new DOMParser();
            return parser.parseFromString(htmlString, "text/html");
        };
    }
    /**
     * @param options
     * options.populateAttachments - populates attachments instead of cid values
     * options.hashAttachment - a function to hash attachment's base64
     * @returns {string}
     */
    getHTML(options = {
        populateAttachments: false,
        hashAttachment: undefined,
    }) {
        return __awaiter(this, void 0, void 0, function* () {
            let html;
            let htmlPart = null;
            for (let i = 0; i < this.parts.length; i++) {
                if (this.parts[i].boundary === "mimemessage") {
                    continue;
                }
                let contentType = getHeaderValue("content-type", this.parts[i]);
                if (contentType === null || contentType === undefined) {
                    continue;
                }
                contentType = contentType[0];
                if (contentType.indexOf("text/html") >= 0) {
                    htmlPart = this.parts[i];
                    break;
                }
            }
            html = decodeMimeBody(htmlPart, this.mimeString);
            if (options.populateAttachments) {
                html = yield this.populateAttachments(html, options.hashAttachment);
            }
            return html.trim();
        });
    }
    /**
     * Functions extracts replied/forwarded content from the HTML
     * @returns {Promise<{ htmlDocument: HTMLDocument, quotedNodes: Array<Node>, quotedPartParent: HTMLElement }>}
     */
    extractQuotedPartFromHTML() {
        return __awaiter(this, void 0, void 0, function* () {
            const html = yield this.getHTML();
            const document = this.parseHTML(html);
            let quotedNodes = null;
            let quotedPartParent = null;
            const inReplyToHeaders = this.getGlobalHeaderValue("in-reply-to");
            if (inReplyToHeaders === null || inReplyToHeaders === void 0 ? void 0 : inReplyToHeaders.length) {
                const mimeProvider = this.determineMimeProvider();
                quotedNodes = this.getQuotedContentElements(mimeProvider, document);
                if (quotedNodes === null || quotedNodes === void 0 ? void 0 : quotedNodes.length) {
                    quotedNodes.forEach((node) => {
                        if (!quotedPartParent) {
                            quotedPartParent = node.parentNode;
                        }
                        else {
                            if (quotedPartParent !== node.parentNode) {
                                throw new Error("Unable to extract quoted part from HTML. Reason: quoted parts having different parent nodes");
                            }
                        }
                    });
                    quotedNodes.forEach((child) => {
                        child.parentNode.removeChild(child);
                    });
                }
                else {
                    quotedNodes = null;
                }
            }
            return { htmlDocument: document, quotedNodes, quotedPartParent };
        });
    }
    getQuotedContentElements(mimeProvider, document) {
        let elements = [];
        if (mimeProvider === constants_1.PROVIDER_GMAIL) {
            const gmailQuoteContainer = utils_1.bfsLookup(document.body, fwdRplyExtractor_1.gmailSeparatorMatcher);
            if (gmailQuoteContainer) {
                elements.push(gmailQuoteContainer);
            }
        }
        else if (mimeProvider === constants_1.PROVIDER_VNCMAIL) {
            let quotedContentSeparator = utils_1.bfsLookup(document.body, fwdRplyExtractor_1.vncMailSeparatorMatcher);
            if (quotedContentSeparator) {
                elements.push(quotedContentSeparator);
                let candidate = quotedContentSeparator.nextSibling;
                while (candidate) {
                    elements.push(candidate);
                    candidate = candidate.nextSibling;
                }
            }
        }
        else if (mimeProvider === constants_1.PROVIDER_OUTLOOK) {
            /**
             * Look up for element with id "appendOnSend". Provided in emails sent by Outlook.com
             */
            let quotedContentSeparator = utils_1.bfsLookup(document.body, fwdRplyExtractor_1.outlookWebSeparatorMatcher);
            /**
             * Look up for a container with specific for Outlook Desktop style
             */
            if (!quotedContentSeparator) {
                let candidate = utils_1.bfsLookup(document.body, fwdRplyExtractor_1.outlookDesktopSeparatorMatcher);
                if (candidate) {
                    // Sometimes, separator might have a div wrapper.
                    // Go up to the tree until we reach root "WordSection1" element.
                    let parent = candidate.parentNode;
                    while (parent && parent.getAttribute("class") !== "WordSection1") {
                        candidate = parent;
                        parent = candidate.parentNode;
                    }
                    quotedContentSeparator = candidate;
                }
            }
            /**
             * Consider separator and everything below it as the quoted content
             */
            if (quotedContentSeparator) {
                elements.push(quotedContentSeparator);
                let candidate = quotedContentSeparator.nextSibling;
                while (candidate) {
                    elements.push(candidate);
                    candidate = candidate.nextSibling;
                }
            }
        }
        return elements;
    }
    /**
     * Function returns MIME provider
     * gmail|outlook|unknown
     */
    determineMimeProvider() {
        const messageIds = this.getGlobalHeaderValue("message-id");
        if (!(messageIds === null || messageIds === void 0 ? void 0 : messageIds.length)) {
            throw new Error("Unable to determine MIME provider. Message ID header is missing.");
        }
        const messageId = messageIds[0].replace(/[<>]/g, "").toLowerCase();
        if (messageId.endsWith(constants_1.PROVIDER_GMAIL)) {
            return constants_1.PROVIDER_GMAIL;
        }
        else if (new RegExp(constants_1.PROVIDER_OUTLOOK_REGEX).test(messageId)) {
            return constants_1.PROVIDER_OUTLOOK;
        }
        else if (new RegExp(constants_1.PROVIDER_VNCMAIL_REGEX).test(messageId)) {
            return constants_1.PROVIDER_VNCMAIL;
        }
        else {
            throw new Error("Unable to determine MIME provider by message ID " + messageId);
        }
    }
    /**
     * @param options
     * options.populateAttachments - populates attachments instead of cid values
     * options.hashAttachment - a function to hash attachment's base64
     * @returns {string}
     */
    getPlain(options = {
        populateAttachments: false,
        hashAttachment: undefined,
    }) {
        return __awaiter(this, void 0, void 0, function* () {
            let plain;
            let plainPart = null;
            for (let i = 0; i < this.parts.length; i++) {
                let contentType = getHeaderValue("content-type", this.parts[i]);
                if (contentType === null || contentType === undefined) {
                    continue;
                }
                contentType = contentType[0];
                if (contentType.indexOf("text/plain") >= 0) {
                    plainPart = this.parts[i];
                    break;
                }
            }
            if (!plainPart) {
                return "";
            }
            plain = decodeMimeBody(plainPart, this.mimeString);
            if (options.populateAttachments) {
                plain = yield this.populateAttachments(plain, options.hashAttachment);
            }
            return plain.trim();
        });
    }
    populateAttachments(targetString, hashFunction) {
        return __awaiter(this, void 0, void 0, function* () {
            for (let i = 0; i < this.parts.length; i++) {
                let contentDisposition = getHeaderValue("content-disposition", this.parts[i]);
                if (contentDisposition === null || contentDisposition === undefined) {
                    continue;
                }
                contentDisposition = contentDisposition[0];
                if (contentDisposition.indexOf("attachment") >= 0 ||
                    contentDisposition.indexOf("inline") >= 0) {
                    const contentIds = getHeaderValue("content-id", this.parts[i]);
                    if (contentIds === null || contentIds === undefined) {
                        continue;
                    }
                    let contentId = contentIds[0];
                    contentId = contentId.slice(1, -1);
                    contentId = "cid:" + contentId;
                    let contentType = getHeaderValue("content-type", this.parts[i]);
                    if (contentType === null || contentType === undefined) {
                        continue;
                    }
                    contentType = contentType[0].split(";")[0];
                    const normalizedBody = getBody(this.mimeString, this.parts[i]).replace(newline, "");
                    let attachmentSrc;
                    if (hashFunction && typeof hashFunction === "function") {
                        attachmentSrc = yield hashFunction(normalizedBody);
                    }
                    else {
                        attachmentSrc = "data:" + contentType + ";base64," + normalizedBody;
                    }
                    targetString = targetString.split(contentId).join(attachmentSrc);
                }
            }
            return targetString;
        });
    }
    getAttachments() {
        const attachments = [];
        for (let i = 0; i < this.parts.length; i++) {
            let contentDisposition = getHeaderValue("content-disposition", this.parts[i]);
            if (contentDisposition === null || contentDisposition === undefined) {
                continue;
            }
            attachments.push(this.parts[i]);
        }
        return attachments;
    }
    getAttachment(part) {
        let contentId = null;
        let contentType = getHeaderValue("content-type", part);
        if (contentType === null || contentType === undefined) {
            return null;
        }
        contentType = contentType[0].split(";")[0].trim();
        let contentTransferEncoding = getHeaderValue("content-transfer-encoding", part);
        if (contentTransferEncoding) {
            contentTransferEncoding = contentTransferEncoding[0];
        }
        else {
            return null;
        }
        let base64;
        if (contentTransferEncoding.toLowerCase() === "base64") {
            base64 = getBody(this.mimeString, part).replace(newline, "");
        }
        else {
            const body = decodeMimeBody(part, this.mimeString);
            base64 = window.btoa(body);
        }
        contentId = getHeaderValue("content-id", part);
        if (contentId) {
            contentId = contentId[0];
        }
        return { contentType, base64, contentId };
    }
    /**
     * Function extracts file name from content type header
     * @param headers
     * @returns {string} ('file.txt')
     */
    getFilenameFromHeaders(headers) {
        const headersToSearch = ["content-type", "content-disposition"];
        const filename = headers &&
            Object.keys(headers)
                .filter((key) => headersToSearch.includes(key))
                .reduce((result, key) => {
                const headerValue = libmime_1.parseHeaderValue(headers[key][0]);
                return (result || headerValue.params.name || headerValue.params.filename);
            }, "");
        return libmime_1.decodeWords(filename) || DEFAULT_ATTACHMENT_NAME;
    }
    extractMessageID() {
        let mime = this.mimeString;
        if (this.mimeString.startsWith("\r\n")) {
            mime = this.mimeString.substring(2); //should not happen
        }
        const headersEndIndex = mime.indexOf("\r\n\r\n"); //the first empty line
        if (headersEndIndex < 0) {
            return null;
        }
        const mimeHeaders = mime.substring(0, headersEndIndex);
        const headers = libmime_1.decodeHeaders(mimeHeaders);
        let messageId = headers["message-id"];
        if (Array.isArray(messageId) && messageId.length > 0) {
            messageId = messageId[0];
        }
        if (messageId && typeof messageId === "string") {
            messageId = messageId.replace(/^</, "").replace(/>$/, "");
            return messageId;
        }
        return null;
    }
    getGlobalHeaderValue(header) {
        for (let i = 0; i < this.parts.length; i++) {
            if (this.parts[i].boundary === "mimemessage") {
                return getHeaderValue(header, this.parts[i]);
            }
        }
        return null;
    }
}
MIMEParser.PROVIDER_GMAIL = constants_1.PROVIDER_GMAIL;
MIMEParser.PROVIDER_OUTLOOK = constants_1.PROVIDER_OUTLOOK;
MIMEParser.PROVIDER_VNCMAIL = constants_1.PROVIDER_VNCMAIL;
MIMEParser.PROVIDER_UNKNOWN = constants_1.PROVIDER_UNKNOWN;
function findAllOccurences(text, subject, from, to) {
    const result = [];
    let index = text.indexOf(subject, from);
    if (index < 0 || index > to) {
        return result;
    }
    result.push(index + 2);
    while (true) {
        index = text.indexOf(subject, index + 1);
        if (index < 0 || index > to) {
            break;
        }
        else {
            result.push(index + 2);
        }
    }
    return result;
}
function findFirstBoundary(body, from, to) {
    if (from >= to) {
        return null;
    }
    const search = "\r\n--";
    let start = body.indexOf(search, from);
    if (start < 0 || start > to) {
        return null;
    }
    start += 2;
    const end = body.indexOf("\r\n", start);
    if (end < 0 || end > to) {
        return null;
    }
    const boundary = body.substring(start, end);
    const boundaryEnd = boundary + "--\r\n";
    const startBoundaryEnd = body.indexOf(boundaryEnd, end);
    if (startBoundaryEnd < 0 || startBoundaryEnd > to) {
        return findFirstBoundary(body, end + 2, to);
    }
    return boundary;
}
function calculateParts(body, from, to, previousBondary) {
    let boundary = findFirstBoundary(body, from, to);
    if (boundary == null) {
        return [{ indices: { from, to }, boundary: previousBondary, leaf: true }];
    }
    const realBoundary = boundary;
    boundary = "\r\n" + boundary;
    const boundaryPairs = [];
    const boundaryIndices = findAllOccurences(body, boundary, from, to);
    const boundaryIndicesLength = boundaryIndices.length;
    for (let i = 0; i < boundaryIndicesLength; i++) {
        const startBoundary = boundaryIndices[i];
        const endBoundary = body.indexOf("\r\n", startBoundary);
        boundaryPairs.push({ start: startBoundary, end: endBoundary });
    }
    let bodies = [];
    if (previousBondary !== null) {
        bodies.push({
            indices: { from, to },
            boundary: previousBondary,
            leaf: false,
        });
    }
    for (let i = 0; i < boundaryIndicesLength - 1; i++) {
        const firstPair = boundaryPairs[i];
        const secondPair = boundaryPairs[i + 1];
        const newFrom = firstPair.end + 2;
        const newTo = secondPair.start - 2;
        const bodyForBoundary = calculateParts(body, newFrom, newTo, realBoundary);
        bodies = bodies.concat(bodyForBoundary);
    }
    return bodies;
}
function parsePartsHeaders(mimeBody, parts) {
    const result = [];
    for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        const indices = part["indices"];
        let headersEnd = mimeBody
            .substring(indices.from, indices.to)
            .indexOf("\r\n\r\n");
        if (headersEnd < 0) {
            headersEnd = indices.from;
        }
        else {
            headersEnd = headersEnd + indices.from + 4;
        }
        part["indices"].headersEnd = headersEnd;
        part["headers"] = libmime_1.decodeHeaders(mimeBody.substring(indices.from, headersEnd));
        result.push(part);
    }
    return result;
}
function fixNewLines(mime) {
    return mime.replace(newline, "\r\n");
}
function getMimeWithoutHeaders(mime) {
    let mimeStart = 0;
    const headersEnd = mime.indexOf("\r\n\r\n"); //the first empty line
    let mimeBody = "";
    if (headersEnd < 0 && mime.startsWith("\r\n")) {
        mime = mime.substring(2); //should not happen
        mimeBody = mime;
        mimeStart = 0;
    }
    else {
        mimeBody = mime.substring(headersEnd + 4);
        mimeStart = headersEnd + 4;
    }
    const headers = libmime_1.decodeHeaders(mime.substring(0, headersEnd));
    const indexOfSMIME = mimeBody.indexOf(SMIMEStart);
    if (indexOfSMIME >= 0) {
        mimeBody = mimeBody.substring(indexOfSMIME + SMIMEStart.length);
        mimeStart += indexOfSMIME + SMIMEStart.length;
    }
    return mimeBody;
}
function parseMIME(mime) {
    mime = fixNewLines(mime);
    let mimeStart = 0;
    const headersEnd = mime.indexOf("\r\n\r\n"); //the first empty line
    let mimeBody = "";
    if (headersEnd < 0 && mime.startsWith("\r\n")) {
        mime = mime.substring(2); //should not happen
        mimeBody = mime;
        mimeStart = 0;
    }
    else {
        mimeBody = mime.substring(headersEnd + 4);
        mimeStart = headersEnd + 4;
    }
    const headers = libmime_1.decodeHeaders(mime.substring(0, headersEnd));
    const indexOfSMIME = mimeBody.indexOf(SMIMEStart);
    if (indexOfSMIME >= 0) {
        mimeBody = mimeBody.substring(indexOfSMIME + SMIMEStart.length);
        mimeStart += indexOfSMIME + SMIMEStart.length;
    }
    mimeBody = "\r\n" + mimeBody + "\r\n";
    const mimeBodyLen = mimeBody.length - 1;
    let parts = calculateParts(mimeBody, 0, mimeBodyLen, null);
    parts = parsePartsHeaders(mimeBody, parts);
    for (let i = 0; i < parts.length; i++) {
        parts[i].indices.from = parts[i].indices.from + (mimeStart - 2);
        parts[i].indices.headersEnd = parts[i].indices.headersEnd + (mimeStart - 2);
        parts[i].indices.to = parts[i].indices.to + (mimeStart - 2);
    }
    parts.push({
        indices: { from: 0, to: mime.length, headersEnd },
        headers,
        boundary: "mimemessage",
        leaf: false,
    });
    return parts;
}
function getHeaderValue(header, part) {
    if (part.headers && part.headers[header] && part.headers[header].length) {
        return (part.headers[header] &&
            part.headers[header].map((header) => libmime_1.decodeWords(header)));
    }
    return null;
}
function getBody(mime, part) {
    if (part.indices === null || part.indices === undefined) {
        return null;
    }
    const indices = part.indices;
    return mime.substring(indices.headersEnd, indices.to);
}
function decodeMimeBody(descriptor, mimeString) {
    const mimeBody = mimeString.slice(descriptor.indices.headersEnd, descriptor.indices.to);
    let contentTransferEncoding = getHeaderValue("content-transfer-encoding", descriptor);
    if (contentTransferEncoding) {
        contentTransferEncoding = contentTransferEncoding[0];
    }
    else {
        return mimeBody;
    }
    let charset = "utf8";
    let contentType = getHeaderValue("content-type", descriptor);
    if (contentType) {
        contentType = contentType[0];
        const parsedContentType = libmime_1.parseHeaderValue(contentType);
        if (parsedContentType &&
            parsedContentType.params &&
            parsedContentType.params.charset) {
            charset = parsedContentType.params.charset;
        }
    }
    let buffer;
    if (contentTransferEncoding.toLowerCase() === "quoted-printable") {
        buffer = libqp_1.decodeQP(mimeBody);
    }
    else if (contentTransferEncoding.toLowerCase() === "base64") {
        buffer = Buffer.from(mimeBody, "base64");
    }
    if (buffer) {
        return charset_1.decodeCharset(buffer, charset);
    }
    return mimeBody;
}
const splitParticipants = (participantsList) => {
    if (!participantsList) {
        return [];
    }
    const participants = participantsList.map((participants) => participants.split(",").map((p) => p.trim()));
    return participants.filter((participant, index) => participants.indexOf(participant) !== index);
};
exports.default = MIMEParser;
